import { ClientApi } from '../../api/client-api';
import { customElement } from 'lit/decorators.js';
import { DataTableWrapper } from '../../../webmodule-common/other/ui/datatable-view';
import { EventSnippet } from '../../../webmodule-common/other/ui/events';
import { getApiFactory } from '../../api/api-injector';
import { html } from 'lit';
import { LangClientListTable } from '../../../localization/scripts/clients/views/client-list-table-lang';
import { RequestPage, ResultPaginated } from '../../../webmodule-common/other/ui/RequestPage';
import { ResultBrowseClientSummary, ViewClientSummary } from '../../api/dealer-api-interface-client';
import { userDataStore } from '../../dealer-franchisee/common/current-user-data-store';
import { WebModuleTable, WebModuleTableColumnDef } from '../../../webmodule-common/components/src/webmodule-components';

export interface ClientSummaryTableBaseOptions {
  title: EventSnippet;
}

@customElement('wm-clientsummarytablebase')
export class ClientSummaryTableBase extends DataTableWrapper<ViewClientSummary> {
  eventTitle: EventSnippet;
  api: ClientApi = getApiFactory().client();
  filter: string | null;
  filterActive: boolean | null;

  constructor(options: ClientSummaryTableBaseOptions) {
    super();
    this.eventTitle = options.title;
    this.filter = null;
  }

  async getRowsFromServer(request: RequestPage): Promise<ResultPaginated<ViewClientSummary>> {
    const results = await this.api.browseClientSummary({
      pageIndex: request.pageIndex,
      pageSize: request.pageSize,
      sortField: request.sortField,
      sortAsc: !request.sortAsc,
      clientOwnerId: userDataStore.clientOwnerId,
      filter: this.filter,
      filterActive: this.filterActive
    });
    if (!results)
      return {
        count: 0,
        pageCount: 0,
        pageIndex: 0,
        pageSize: this.pageLength(),
        results: []
      };

    await this.doPreFetching(results);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- It is not null

    return results.clientSummary;
  }

  async doPreFetching(results: ResultBrowseClientSummary) {
    await Promise.all(this.getPreFetched(results));
  }

  enableFiltering(): boolean {
    return true;
  }

  updateFilter(_searchTerm: string | null) {
    this.filter = _searchTerm;
  }

  getColumns(): WebModuleTableColumnDef[] {
    return [
      {
        title: () => LangClientListTable.client(),
        fieldName: 'name',
        sortable: true,
        // Sizing moves from Bootstrap `colpx-*` classes (which don't pierce the
        // new table's shadow DOM) to the column's own width model.
        minWidth: '220px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewClientSummary;

          return html`${rowItem.name}`;
        }
      },
      {
        title: () => LangClientListTable.primaryContact(),
        fieldName: 'primaryContactName',
        sortable: true,
        minWidth: '200px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewClientSummary;

          return html`${rowItem.primaryContactName}`;
        }
      },
      {
        title: () => LangClientListTable.contactEmail(),
        fieldName: 'primaryContactEmail',
        minWidth: '220px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewClientSummary;

          return html`${rowItem.primaryContactEmail}`;
        }
      },
      {
        title: () => LangClientListTable.address(),
        fieldName: 'physicalAddress',
        minWidth: '300px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewClientSummary;
          const physicalAddress = rowItem.physicalAddress;

          if (physicalAddress)
            return html`${[
              physicalAddress.line1,
              physicalAddress.locality,
              physicalAddress.region,
              physicalAddress.postcode,
              physicalAddress.country
            ]
              .filter(Boolean)
              .join(', ')}`;

          return '';
        }
      }
    ];
  }

  protected getPreFetched(_results: ResultBrowseClientSummary): Promise<void>[] {
    return [];
  }
}
